import { lighten } from 'polished';
import { StylesConfig } from 'react-select';
import styled, { css } from 'styled-components';

interface UserAvatarProps {
  avatarSrc: string;
}

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 2.38rem 2.38rem;

  @media (max-width: 1024px) {
    padding: 1rem 2.38rem;
  }
`;

export const LeftSideContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;

  > svg {
    margin-right: 0.88rem;
  }

  > h1 {
    text-align: center;
    font-family: 'RedHatDisplay-Extrabold';
    color: #1e88e5;
    font-size: 2rem;
    line-height: 2.55rem;
    margin-right: 0.88rem;
  }

  > div > svg {
    display: block;
    margin: auto;
    position: relative;
    cursor: pointer;
    transition: stroke 0.3s;
  }
`;

export const UserAvatar = styled.div<UserAvatarProps>`
  width: 10vw;
  height: 10vw;
  max-width: 50px;
  max-height: 50px;
  min-width: 30px;
  min-height: 30px;
  border-radius: 50%;
  background: url('${props => props.avatarSrc}') no-repeat center;
  background-size: cover;
`;

/**
 * Medidas do botão principal desta tela.
 *
 * O PrimaryButton não traz padding, altura nem raio — quem o usa é que decide
 * o tamanho. Isto fica separado porque a edição de projeto precisa das mesmas
 * medidas e não pode herdá-las: lá o botão fica dentro de uma linha de ações,
 * junto do cancelar, e o seletor de filho direto não o alcança.
 */
export const primaryButtonSizing = css`
  height: 3.55rem;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-items: center;
  font-family: 'RedHatDisplay-Bold';
  font-size: 1rem;
  border-radius: 0.6rem;
  box-shadow: 0px 3px 15px rgba(255, 152, 0, 0.3);

  > svg {
    margin-right: 0.6rem;
  }
`;

export const InputInfoPost = styled.div`
  width: 50rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  @media (max-width: 1024px) {
    width: 100%;
  }

  > form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    > button {
      margin-top: 1.8rem;
    }

    > .primary-button {
      ${primaryButtonSizing}
    }
  }
`;

export const Basicnfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.6rem;

  background-color: #fff;
  border-radius: 1.38rem;
  border: 1px solid #e2e2e2;

  > header {
    display: flex;
    flex-direction: row;
    align-items: center;

    > div {
      margin-left: 1.5rem;

      > h2 {
        font-family: 'RedHatDisplay-Bold';
        font-size: 1.5rem;
      }

      > span {
        font-family: 'RedHatDisplay-Regular';
        color: #63646a;
      }
    }
  }

  > section {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.5rem;

    > div {
      width: 16rem;
      > h4 {
        margin-bottom: 0.6rem;
      }

      &:first-child {
        width: 23rem;
      }
    }
  }

  @media (max-width: 1024px) {
    > section {
      flex-direction: column;

      /*
       * O :first-child precisa ser repetido aqui.
       * Empilhado, todo campo ocupa a linha inteira — mas o primeiro leva um
       * width de 23rem declarado fora desta media query, e um seletor com
       * :first-child tem especificidade maior que "> div". Sem repeti-lo, só
       * o primeiro campo ficava mais estreito que os outros e, por causa do
       * align-items: center da seção, aparecia centralizado e desalinhado do
       * resto do formulário.
       */
      > div,
      > div:first-child {
        width: 100%;

        & > *:nth-child(n + 1) {
          margin-top: 1rem;
        }
      }
    }
  }

  > div {
    margin-top: 1.5rem;
    > h4 {
      margin-bottom: 0.6rem;
    }
  }
`;

export const InfoPost = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.6rem;
  margin-top: 1.5rem;

  background-color: #fff;
  border-radius: 1.38rem;
  border: 1px solid #e2e2e2;

  > div {
    margin-bottom: 1.5rem;
    > h4 {
      margin-bottom: 0.6rem;
    }
  }
`;

export const toolSelectStyles: StylesConfig<
  { label: string; value: string },
  false
> = {
  indicatorSeparator: provided => ({
    ...provided,
    display: 'none',
  }),
  container: provided => ({
    ...provided,
    fontFamily: 'RedHatDisplay-Regular',
    fontSize: '1rem',
  }),
  control: (provided, state) => ({
    ...provided,
    paddingLeft: '1.1rem',
    margin: 0,
    height: 'auto',
    borderRadius: '0.56rem',
    border:
      state.isFocused || state.selectProps.menuIsOpen
        ? '1px solid rgba(255,152,0,1)'
        : '1px solid #e2e2e2',
    boxShadow: 'none', // Remove o box-shadow padrão
    '&:hover': {
      border: '1px solid rgba(255,152,0,1)',
      boxShadow: 'none',
    },
  }),
  valueContainer: provided => ({
    ...provided,
    padding: '0 1.167rem', // sem ponto‑vírgula no final
    height: '2.567rem',
    color: '#1e88e5',
  }),
  input: provided => ({
    ...provided,
  }),
  option: (provided, state) => ({
    ...provided,
    color: '#1e88e5',
    background: state.isFocused ? 'rgba(226,226,226,0.2)' : '#fff',
  }),
  singleValue: provided => ({
    ...provided,
    color: '#1e88e5',
  }),
  placeholder: provided => ({
    ...provided,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    margin: 0,
    padding: 0,
    stroke: state.isFocused ? 'rgba(255,152,0,1)' : '#1e88e5',
  }),
  indicatorsContainer: provided => ({
    ...provided,
    height: 'auto',
    margin: 0,
    padding: '0 2rem',
  }),
};

export const subjectsSelectStyles: StylesConfig<
  { label: string; value: string },
  false
> = {
  indicatorSeparator: providedStyles => ({
    ...providedStyles,
    display: 'none',
  }),
  clearIndicator: providedStyles => ({
    ...providedStyles,
    display: 'none',
  }),
  container: providedStyles => ({
    ...providedStyles,
    fontFamily: 'MarkPro',
    fontSize: '1rem',
  }),
  control: (providedStyles, state) => ({
    ...providedStyles,
    padding: 0,
    margin: 0,
    height: 'auto',
    borderRadius: '0.56rem',
    border:
      state.isFocused || state.selectProps.menuIsOpen
        ? '1px solid rgba(255,152,0,1)'
        : '1px solid #e2e2e2',
    boxShadow: 'none', // Remove o box-shadow padrão
    '&:hover': {
      border: '1px solid rgba(255,152,0,1)',
      boxShadow: 'none',
    },
  }),
  valueContainer: providedStyles => ({
    ...providedStyles,
    padding: '0 1.167rem', // sem ponto‑vírgula no final
    height: '2.567rem',
    color: '#1e88e5',
  }),
  input: providedStyles => ({
    ...providedStyles,
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1.28rem',
    height: '100%',
    margin: 0,
  }),
  option: (providedStyles, state) => ({
    ...providedStyles,
    color: '#1e88e5',
    background: state.isFocused ? '#e9e9e9' : '#fff',
  }),
  placeholder: providedStyles => ({
    ...providedStyles,
    lineHeight: '0.88rem',
    height: 'auto',
    margin: 0,
  }),
  dropdownIndicator: (providedStyles, state) => ({
    ...providedStyles,
    margin: 0,
    padding: 0,
    stroke: state.isFocused ? '#ff9800' : '#1e88e5',
  }),
  indicatorsContainer: providedStyles => ({
    ...providedStyles,
    height: 'auto',
    margin: 0,
    padding: '0px 2rem',
  }),
};

export const RightSideContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e2e2e2;
  margin-left: 1rem;
  padding: 1rem;
`;

export const ChallengeSubmit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  > h1 {
    font-family: 'RedHatDisplay-Bold';
    font-size: 1.4rem;
    color: #1e88e5;
    display: flex;
    align-items: center;
    margin-bottom: 0.2rem;

    > svg {
      margin-right: 0.4rem;
    }
  }
`;

export const SubmitHint = styled.p`
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.85rem;
  line-height: 1.4;
  color: #607d8b;
  background: #eceff1;
  border: 1px solid #cfd8dc;
  border-radius: 0.4rem;
  padding: 0.6rem 0.8rem;
`;
